import React, { useState } from 'react';
import { DollarSign, ShieldCheck, Info, Globe, Layers, ArrowRight, BarChart2 } from 'lucide-react';
import { OFFICIAL_INFLATION_INDICES } from '../../data/wealthData';

export const InflationPurchasingPowerView: React.FC = () => {
  const [currentAmount, setCurrentAmount] = useState<number>(100000);
  const [inflationRatePct, setInflationRatePct] = useState<number>(2.5);
  const [horizonYears, setHorizonYears] = useState<number>(20);

  const futureNominalNeeded = currentAmount * Math.pow(1 + inflationRatePct / 100, horizonYears);
  const futurePurchasingPower = currentAmount / Math.pow(1 + inflationRatePct / 100, horizonYears);
  const erosionPct = (1 - futurePurchasingPower / currentAmount) * 100;

  return (
    <div className="max-w-7xl mx-auto space-y-8 pb-16">
      {/* Header Banner */}
      <div className="bg-gradient-to-r from-teal-900 via-slate-900 to-slate-900 rounded-2xl p-6 sm:p-8 text-white shadow-xl">
        <div className="space-y-3">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-teal-500/20 text-teal-300 text-xs font-semibold border border-teal-500/30">
            <BarChart2 className="w-3.5 h-3.5" />
            <span>YamooPay Global Inflation Intelligence Engine</span>
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">
            Official Inflation Statistics & Purchasing Power Intelligence
          </h1>
          <p className="text-slate-300 text-sm max-w-2xl leading-relaxed">
            Analyze historical Consumer Price Indices (CPI) from official statistical authorities, simulate future purchasing power erosion, and calculate inflation-adjusted real value.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        {/* Purchasing Power Erosion Simulator */}
        <div className="lg:col-span-5 bg-white rounded-2xl border border-slate-200 p-6 shadow-sm space-y-6">
          <h2 className="text-base font-bold text-slate-900 border-b border-slate-100 pb-3">Purchasing Power Erosion Calculator</h2>

          <div className="space-y-4 text-xs text-slate-700">
            <div>
              <label className="block font-semibold text-slate-900 mb-1">Current Purchasing Amount ($)</label>
              <input
                type="number"
                value={currentAmount}
                onChange={(e) => setCurrentAmount(parseFloat(e.target.value) || 0)}
                className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg font-bold text-slate-900 focus:outline-none focus:ring-2 focus:ring-teal-500"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block font-semibold text-slate-900 mb-1">Assumed Inflation (%/yr)</label>
                <input
                  type="number"
                  step="0.1"
                  value={inflationRatePct}
                  onChange={(e) => setInflationRatePct(parseFloat(e.target.value) || 0)}
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg font-semibold text-slate-900 focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </div>

              <div>
                <label className="block font-semibold text-slate-900 mb-1">Time Horizon (Years)</label>
                <input
                  type="number"
                  min="1"
                  max="50"
                  value={horizonYears}
                  onChange={(e) => setHorizonYears(parseInt(e.target.value) || 1)}
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg font-semibold text-slate-900 focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </div>
            </div>

            <div className="bg-teal-50/60 border border-teal-200 rounded-xl p-4 space-y-3">
              <div className="text-xs text-teal-800 font-semibold">Simulation Outcome ({horizonYears} Years)</div>
              <div className="space-y-1">
                <div className="text-xs text-slate-600">Future Purchasing Power of ${currentAmount.toLocaleString()} today:</div>
                <div className="text-2xl font-extrabold text-teal-900">${Math.round(futurePurchasingPower).toLocaleString()}</div>
                <div className="text-[11px] text-teal-700 font-medium">Erosion of purchasing power: -{erosionPct.toFixed(1)}%</div>
              </div>
              <div className="pt-2 border-t border-teal-200/60 text-xs text-slate-700">
                Nominal sum required in {horizonYears} years to equal ${currentAmount.toLocaleString()} today: <span className="font-bold text-slate-900">${Math.round(futureNominalNeeded).toLocaleString()}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Official CPI Data Table Column */}
        <div className="lg:col-span-7 bg-white rounded-2xl border border-slate-200 p-6 shadow-sm space-y-4">
          <div className="flex items-center justify-between border-b border-slate-100 pb-3">
            <div className="flex items-center gap-2">
              <Globe className="w-4 h-4 text-teal-600" />
              <h3 className="text-base font-bold text-slate-900">Official Canonical CPI Indices</h3>
            </div>
            <span className="text-xs text-emerald-700 font-semibold flex items-center gap-1">
              <ShieldCheck className="w-3.5 h-3.5" />
              Verified Official Authorities
            </span>
          </div>

          <div className="border border-slate-200 rounded-xl overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead className="bg-slate-50 text-slate-700 font-semibold border-b border-slate-200">
                <tr>
                  <th className="py-2.5 px-3">Country / Region</th>
                  <th className="py-2.5 px-3">Official Index & Authority</th>
                  <th className="py-2.5 px-3">Latest Rate</th>
                  <th className="py-2.5 px-3 text-right">30-Yr Avg</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 text-slate-800">
                {OFFICIAL_INFLATION_INDICES.map((item) => (
                  <tr key={item.id} className="hover:bg-slate-50">
                    <td className="py-2.5 px-3 font-semibold text-slate-900">{item.countryName}</td>
                    <td className="py-2.5 px-3">
                      <div className="font-medium text-slate-900">{item.indexName}</div>
                      <div className="text-[10px] text-slate-500">{item.source} ({item.latestPeriod})</div>
                    </td>
                    <td className="py-2.5 px-3 font-bold text-teal-700">{item.latestAnnualRatePct}%</td>
                    <td className="py-2.5 px-3 text-right font-semibold text-slate-700">{item.historical30yrAvgPct}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};
